import { useState, useCallback } from 'react';
import { Globe, Sparkles } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { useAIAnalysis } from '../../hooks/useAIAnalysis';
import { TextInput, TextArea, Select } from '../ui/FormField';
import { validateUrl } from '../../lib/validators';
import type { Archetype, Industry, ReferenceSite } from '../../types/design-spec';
import { generateId } from '../../utils/helpers';

const INDUSTRIES: { value: Industry; label: string }[] = [
  { value: 'property', label: '房地產' },
  { value: 'medical', label: '醫療' },
  { value: 'restaurant', label: '餐飲' },
  { value: 'retail', label: '零售' },
  { value: 'consulting', label: '顧問諮詢' },
  { value: 'education', label: '教育' },
  { value: 'technology', label: '科技' },
  { value: 'finance', label: '金融' },
  { value: 'legal', label: '法律' },
  { value: 'creative', label: '創意' },
  { value: 'nonprofit', label: '非牟利' },
  { value: 'other', label: '其他' },
];

const ARCHETYPES: { value: Archetype; label: string }[] = [
  { value: 'landing', label: 'Landing Page' },
  { value: 'cms', label: 'CMS 內容管理' },
  { value: 'saas', label: 'SaaS' },
  { value: 'blog', label: '部落格' },
  { value: 'ecommerce', label: '電商' },
  { value: 'portfolio', label: '作品集' },
];

export function ReferenceInput() {
  const config = useProjectStore((state) => state.config);
  const setConfig = useProjectStore((state) => state.setConfig);
  const setActiveTab = useProjectStore((state) => state.setActiveTab);
  const setReferences = useProjectStore((state) => state.setReferences);

  const { analyze, loading, error: analysisError } = useAIAnalysis();


  const [urls, setUrls] = useState<string[]>(config.references.map((r) => r.url).concat(['', '', '', '', '']).slice(0, 5));
  const [industry, setIndustry] = useState<Industry>(config.industry);
  const [archetype, setArchetype] = useState<Archetype>(config.archetype);
  const [brandName, setBrandName] = useState(config.brand.nameEn || config.brand.nameCn || '');
  const [brandTagline, setBrandTagline] = useState(config.brand.tagline || '');
  const [brandDescription, setBrandDescription] = useState(config.brand.description || '');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    const filledUrls = urls.map((u) => u.trim()).filter(Boolean);

    if (filledUrls.length < 1) {
      errors.urls = '請至少輸入 1 個參考網站 URL';
    } else if (filledUrls.length > 5) {
      errors.urls = '最多可輸入 5 個參考網站 URL';
    } else {
      const invalid = filledUrls.filter((u) => !validateUrl(u));
      if (invalid.length > 0) {
        errors.urls = `無效的 URL: ${invalid.join(', ')}`;
      }
    }

    if (!brandName.trim()) {
      errors.brandName = '請輸入品牌名稱';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [urls, brandName]);

  const handleAnalyze = async () => {
    if (!validate()) return;

    const filledUrls = urls.map((u) => u.trim()).filter(Boolean);
    const refs: ReferenceSite[] = filledUrls.map((url) => ({ id: generateId(), url }));

    setConfig({
      archetype,
      industry,
      brand: {
        ...config.brand,
        nameEn: brandName,
        nameCn: brandName,
        tagline: brandTagline,
        description: brandDescription,
      },
    });
    setReferences(refs);

    await analyze({
      urls: filledUrls,
      industry,
      archetype,
      brandInfo: {
        name: brandName,
        tagline: brandTagline,
        description: brandDescription,
      },
    });

    setActiveTab('analysis');
  };

  const updateUrl = (index: number, value: string) => {
    const next = [...urls];
    next[index] = value;
    setUrls(next);
  };

  return (
    <div className="max-w-3xl mx-auto pb-10">
      <div className="flex items-center gap-3 mb-6">
        <Globe className="text-blue-600" size={28} />
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">參考網站分析</h2>
      </div>

      <p className="text-slate-600 dark:text-slate-300 mb-6">
        輸入 1-5 個你喜歡的參考網站，AI 會分析它們的設計風格並生成 Design Spec。
      </p>

      <div className="space-y-6 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
            參考網站 URL <span className="text-slate-400 font-normal">（1-5 個）</span>
          </label>
          <div className="space-y-3">
            {urls.map((url, index) => (
              <TextInput
                key={index}
                label=""
                value={url}
                onChange={(e) => updateUrl(index, e.target.value)}
                placeholder="https://example.com"
              />
            ))}
          </div>
          {validationErrors.urls && (
            <p className="mt-2 text-sm text-red-500">{validationErrors.urls}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="行業"
            value={industry}
            onChange={(e) => setIndustry(e.target.value as Industry)}
            options={INDUSTRIES}
          />
          <Select
            label="網站類型"
            value={archetype}
            onChange={(e) => setArchetype(e.target.value as Archetype)}
            options={ARCHETYPES}
          />
        </div>

        <TextInput
          label="品牌名稱"
          value={brandName}
          onChange={(e) => setBrandName(e.target.value)}
          error={validationErrors.brandName}
          required
        />
        <TextInput
          label="品牌標語"
          value={brandTagline}
          onChange={(e) => setBrandTagline(e.target.value)}
        />
        <TextArea
          label="品牌描述"
          value={brandDescription}
          onChange={(e) => setBrandDescription(e.target.value)}
          rows={3}
        />

        {analysisError && (
          <div className="p-3 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
            {analysisError}
          </div>
        )}

        <button
          type="button"
          onClick={handleAnalyze}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold transition-colors"
        >
          <Sparkles size={18} className={loading ? 'animate-spin' : ''} />
          {loading ? '分析中...' : '分析參考網站'}
        </button>
      </div>
    </div>
  );
}
